import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Heart,
  ClipboardList,
  MessageCircleQuestion,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  X,
  Stethoscope,
  Home,
  Building,
  Siren,
  Clock,
  ShieldCheck,
  Volume2,
  VolumeX,
  Music,
} from "lucide-react";

// Animated sound wave bars component
const SoundWaveIndicator = ({ isPlaying }: { isPlaying: boolean }) => (
  <div className="flex items-end gap-0.5 h-4">
    {[1, 2, 3, 4].map((bar) => (
      <motion.div
        key={bar}
        className="w-1 rounded-full bg-gradient-to-t from-purple-500 to-indigo-400"
        animate={isPlaying ? {
          height: ["4px", "16px", "8px", "14px", "4px"],
        } : { height: "4px" }}
        transition={isPlaying ? {
          duration: 0.8 + bar * 0.1,
          repeat: Infinity,
          ease: "easeInOut",
          delay: bar * 0.1,
        } : { duration: 0.2 }}
      />
    ))}
  </div>
);

// Background music URL
const BACKGROUND_MUSIC_URL = "/audio/demo-background.mp3";

interface PlatformDemoModalProps {
  open: boolean;
  onClose: () => void;
}

interface DemoStep {
  id: string;
  stepNumber: number;
  title: string;
  titleTranslations: { hi: string; pa: string; bn: string };
  subtitle: string;
  description: string;
  descriptionTranslations: { hi: string; pa: string; bn: string };
  icon: React.ElementType;
  accentColor: string;
  mockContent: React.ReactNode;
}

const demoSteps: DemoStep[] = [
  {
    id: "welcome",
    stepNumber: 1,
    title: "Sign In Securely",
    titleTranslations: {
      hi: "सुरक्षित रूप से साइन इन करें",
      pa: "ਸੁਰੱਖਿਅਤ ਢੰਗ ਨਾਲ ਸਾਈਨ ਇਨ ਕਰੋ",
      bn: "নিরাপদে সাইন ইন করুন"
    },
    subtitle: "Quick & Easy Access",
    description: "Enter your email and we'll send you a secure one-time code. No passwords to remember!",
    descriptionTranslations: {
      hi: "अपना ईमेल दर्ज करें और हम आपको एक सुरक्षित वन-टाइम कोड भेजेंगे।",
      pa: "ਆਪਣਾ ਈਮੇਲ ਦਰਜ ਕਰੋ ਅਤੇ ਅਸੀਂ ਤੁਹਾਨੂੰ ਇੱਕ ਸੁਰੱਖਿਅਤ ਵਨ-ਟਾਈਮ ਕੋਡ ਭੇਜਾਂਗੇ।",
      bn: "আপনার ইমেল লিখুন এবং আমরা আপনাকে একটি নিরাপদ ওয়ান-টাইম কোড পাঠাব।"
    },
    icon: ShieldCheck,
    accentColor: "from-emerald-500 to-teal-600",
    mockContent: (
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-white/10 border border-white/20">
          <p className="text-xs text-slate-400 mb-2">Email Address</p>
          <div className="h-10 rounded-lg bg-white/20 flex items-center px-3">
            <span className="text-white/70 text-sm">you@email.com</span>
          </div>
        </div>
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="p-4 rounded-xl bg-emerald-500/20 border border-emerald-500/30"
        >
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <span className="text-emerald-300 text-sm">Code sent! Check your inbox</span>
          </div>
        </motion.div>
      </div>
    ),
  },
  {
    id: "chief-complaint",
    stepNumber: 2,
    title: "What's Bothering You?",
    titleTranslations: {
      hi: "आपको क्या परेशान कर रहा है?",
      pa: "ਤੁਹਾਨੂੰ ਕੀ ਪਰੇਸ਼ਾਨ ਕਰ ਰਿਹਾ ਹੈ?",
      bn: "আপনাকে কী বিরক্ত করছে?"
    },
    subtitle: "Tell Us Your Main Concern",
    description: "Select from common symptoms or describe your concern in your own words. Be as specific as you can.",
    descriptionTranslations: {
      hi: "सामान्य लक्षणों में से चुनें या अपनी चिंता अपने शब्दों में बताएं।",
      pa: "ਆਮ ਲੱਛਣਾਂ ਵਿੱਚੋਂ ਚੁਣੋ ਜਾਂ ਆਪਣੀ ਚਿੰਤਾ ਆਪਣੇ ਸ਼ਬਦਾਂ ਵਿੱਚ ਦੱਸੋ।",
      bn: "সাধারণ লক্ষণ থেকে বেছে নিন বা আপনার উদ্বেগ নিজের ভাষায় বর্ণনা করুন।"
    },
    icon: ClipboardList,
    accentColor: "from-blue-500 to-indigo-600",
    mockContent: (
      <div className="space-y-4">
        <p className="text-xs text-slate-400">Select a symptom or type your own:</p>
        <div className="flex flex-wrap gap-2">
          {["Headache", "Chest pain", "Fever", "Cough", "Back pain"].map((symptom, i) => (
            <motion.div
              key={symptom}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 + i * 0.1 }}
            >
              <Badge 
                variant={symptom === "Headache" ? "default" : "outline"}
                className={symptom === "Headache" 
                  ? "bg-blue-500 text-white cursor-pointer" 
                  : "border-white/30 text-white/70 cursor-pointer hover:bg-white/10"
                }
              >
                {symptom}
              </Badge>
            </motion.div>
          ))}
        </div>
        <div className="p-3 rounded-lg bg-white/10 border border-white/20">
          <span className="text-white/50 text-sm italic">"I've had a headache for 2 days..."</span>
        </div>
      </div>
    ),
  },
  {
    id: "questions",
    stepNumber: 3,
    title: "Quick Health Questions",
    titleTranslations: {
      hi: "त्वरित स्वास्थ्य प्रश्न",
      pa: "ਤੇਜ਼ ਸਿਹਤ ਸਵਾਲ",
      bn: "দ্রুত স্বাস্থ্য প্রশ্ন"
    },
    subtitle: "Help Us Understand Better",
    description: "Answer a few simple questions about your symptoms, severity, and medical history. Takes just 2-3 minutes.",
    descriptionTranslations: {
      hi: "अपने लक्षणों, गंभीरता और चिकित्सा इतिहास के बारे में कुछ सवालों का जवाब दें।",
      pa: "ਆਪਣੇ ਲੱਛਣਾਂ, ਗੰਭੀਰਤਾ ਅਤੇ ਮੈਡੀਕਲ ਇਤਿਹਾਸ ਬਾਰੇ ਕੁਝ ਸਵਾਲਾਂ ਦੇ ਜਵਾਬ ਦਿਓ।",
      bn: "আপনার লক্ষণ, তীব্রতা এবং চিকিৎসা ইতিহাস সম্পর্কে কয়েকটি প্রশ্নের উত্তর দিন।"
    },
    icon: MessageCircleQuestion,
    accentColor: "from-purple-500 to-violet-600",
    mockContent: (
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-white/10 border border-white/20">
          <p className="text-sm text-white mb-3">How severe is your pain?</p>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <motion.div
                key={n}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3 + n * 0.05 }}
                className={`flex-1 h-8 rounded flex items-center justify-center text-xs font-medium cursor-pointer ${
                  n <= 4 
                    ? "bg-purple-500/60 text-white" 
                    : "bg-white/10 text-white/50"
                }`}
              >
                {n}
              </motion.div>
            ))}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-slate-400">Mild</span>
            <span className="text-xs text-slate-400">Severe</span>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex items-center gap-2 text-slate-400 text-xs"
        >
          <Clock className="h-3 w-3" />
          <span>About 2 minutes remaining</span>
        </motion.div>
      </div>
    ),
  },
  {
    id: "safety-check",
    stepNumber: 4,
    title: "Safety Check",
    titleTranslations: {
      hi: "सुरक्षा जांच",
      pa: "ਸੁਰੱਖਿਆ ਜਾਂਚ",
      bn: "নিরাপত্তা পরীক্ষা"
    },
    subtitle: "Emergency Warning Signs",
    description: "We check for any urgent symptoms that need immediate attention. Your safety is our top priority.",
    descriptionTranslations: {
      hi: "हम किसी भी तत्काल लक्षणों की जांच करते हैं जिन्हें तुरंत ध्यान देने की आवश्यकता है।",
      pa: "ਅਸੀਂ ਕਿਸੇ ਵੀ ਜ਼ਰੂਰੀ ਲੱਛਣਾਂ ਦੀ ਜਾਂਚ ਕਰਦੇ ਹਾਂ ਜਿਨ੍ਹਾਂ ਨੂੰ ਤੁਰੰਤ ਧਿਆਨ ਦੀ ਲੋੜ ਹੈ।",
      bn: "আমরা যেকোনো জরুরি লক্ষণ পরীক্ষা করি যেগুলির তাৎক্ষণিক মনোযোগ প্রয়োজন।"
    },
    icon: AlertTriangle,
    accentColor: "from-amber-500 to-orange-600",
    mockContent: (
      <div className="space-y-4">
        <p className="text-xs text-slate-400">Do you have any of these symptoms?</p>
        <div className="space-y-2">
          {[
            { text: "Difficulty breathing", checked: false },
            { text: "Chest pain or pressure", checked: false },
            { text: "Sudden confusion", checked: false },
            { text: "Severe bleeding", checked: false },
          ].map((item, i) => (
            <motion.div
              key={item.text}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10"
            >
              <div className="w-5 h-5 rounded border-2 border-white/30" />
              <span className="text-white/80 text-sm">{item.text}</span>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="p-3 rounded-lg bg-emerald-500/20 border border-emerald-500/30"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm">No emergency signs detected</span>
          </div>
        </motion.div>
      </div>
    ),
  },
  {
    id: "recommendation",
    stepNumber: 5,
    title: "Your Care Recommendation",
    titleTranslations: {
      hi: "आपकी देखभाल की सिफारिश",
      pa: "ਤੁਹਾਡੀ ਦੇਖਭਾਲ ਦੀ ਸਿਫਾਰਸ਼",
      bn: "আপনার যত্নের সুপারিশ"
    },
    subtitle: "Personalized Guidance",
    description: "Based on your answers, we'll tell you the best next step—whether it's home care, seeing a doctor, or getting urgent help.",
    descriptionTranslations: {
      hi: "आपके जवाबों के आधार पर, हम आपको सबसे अच्छा अगला कदम बताएंगे।",
      pa: "ਤੁਹਾਡੇ ਜਵਾਬਾਂ ਦੇ ਆਧਾਰ 'ਤੇ, ਅਸੀਂ ਤੁਹਾਨੂੰ ਸਭ ਤੋਂ ਵਧੀਆ ਅਗਲਾ ਕਦਮ ਦੱਸਾਂਗੇ।",
      bn: "আপনার উত্তরের উপর ভিত্তি করে, আমরা আপনাকে সেরা পরবর্তী পদক্ষেপ বলব।"
    },
    icon: Heart,
    accentColor: "from-rose-500 to-pink-600",
    mockContent: (
      <div className="space-y-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, type: "spring" }}
          className="p-5 rounded-2xl bg-gradient-to-br from-teal-500/30 to-emerald-500/20 border border-teal-500/40"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-full bg-teal-500">
              <Stethoscope className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-teal-300 text-xs">Recommendation</p>
              <p className="text-white font-semibold">See Your Doctor</p>
            </div>
          </div>
          <p className="text-white/70 text-sm">
            Schedule an appointment within the next few days for evaluation.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-4 gap-2">
          {[
            { icon: Home, label: "Home Care", active: false },
            { icon: Stethoscope, label: "Doctor", active: true },
            { icon: Building, label: "Urgent Care", active: false },
            { icon: Siren, label: "ER", active: false },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className={`p-2 rounded-lg text-center ${
                  item.active 
                    ? "bg-teal-500/30 border border-teal-500/50" 
                    : "bg-white/5 border border-white/10"
                }`}
              >
                <Icon className={`h-4 w-4 mx-auto mb-1 ${item.active ? "text-teal-400" : "text-white/40"}`} />
                <p className={`text-xs ${item.active ? "text-teal-300" : "text-white/40"}`}>{item.label}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    ),
  },
];

export default function PlatformDemoModal({ open, onClose }: PlatformDemoModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.1);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const step = demoSteps[currentStep];
  const StepIcon = step.icon;

  // Start background music
  const startMusic = () => {
    if (audioRef.current) return; // Already playing
    
    try {
      const audio = new Audio(BACKGROUND_MUSIC_URL);
      audio.loop = true;
      audio.volume = volume;
      audio.play().then(() => {
        setIsMusicPlaying(true);
      }).catch((error) => {
        console.log("Autoplay blocked, waiting for user interaction:", error);
      });
      audioRef.current = audio;
    } catch (error) {
      console.log("Audio playback error:", error);
    }
  };

  // Stop background music
  const stopMusic = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsMusicPlaying(false);
  };

  // Auto-start music when modal opens, stop when it closes
  useEffect(() => {
    if (open) {
      startMusic();
    } else {
      stopMusic();
    }
    return () => stopMusic();
  }, [open]);

  // Handle mute/volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [isMuted, volume]);

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
    // Start audio on user interaction
    if (!audioRef.current && open) {
      startMusic();
    }
  };

  // Try to play music on user interaction
  const tryPlayMusic = () => {
    if (!audioRef.current && open) {
      startMusic();
    }
  };

  // Auto-advance timer
  useEffect(() => {
    if (!open || !isAutoPlaying) {
      setProgress(0);
      return;
    }

    const duration = 5000;
    const interval = 50;
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += interval;
      setProgress((elapsed / duration) * 100);

      if (elapsed >= duration) {
        setCurrentStep((prev) => (prev + 1) % demoSteps.length);
        elapsed = 0;
        setProgress(0);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [open, isAutoPlaying, currentStep]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setIsAutoPlaying(true);
      setProgress(0);
    }
  }, [open]);

  const goToStep = (index: number) => {
    setCurrentStep(index);
    setProgress(0);
  };

  const goNext = () => {
    setCurrentStep((prev) => (prev + 1) % demoSteps.length);
    setProgress(0);
  };

  const goPrev = () => {
    setCurrentStep((prev) => (prev - 1 + demoSteps.length) % demoSteps.length);
    setProgress(0);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] sm:w-full p-0 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
        {/* Header */}
        <div className="relative p-4 sm:p-6 pb-3 sm:pb-4">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 sm:right-4 top-2 sm:top-4 text-slate-400 hover:text-white hover:bg-white/10"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>

          <div className="text-center mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">How It Works</h2>
            
            {/* Multi-language translations */}
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-slate-400 text-xl mb-2">
              <span className="font-hindi">यह कैसे काम करता है</span>
              <span className="text-slate-500">•</span>
              <span className="font-punjabi">ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ</span>
              <span className="text-slate-500">•</span>
              <span className="font-bengali">এটি কিভাবে কাজ করে</span>
            </div>
            
            <p className="text-sm sm:text-base text-slate-400">Your health assessment in 5 simple steps</p>
            
            {/* Multi-language translations for subtitle */}
            <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 text-slate-500 text-base mt-1">
              <span className="font-hindi">5 सरल चरणों में आपका स्वास्थ्य मूल्यांकन</span>
              <span className="text-slate-600">•</span>
              <span className="font-punjabi">5 ਸਰਲ ਕਦਮਾਂ ਵਿੱਚ ਤੁਹਾਡਾ ਸਿਹਤ ਮੁਲਾਂਕਣ</span>
              <span className="text-slate-600">•</span>
              <span className="font-bengali">5 সহজ ধাপে আপনার স্বাস্থ্য মূল্যায়ন</span>
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-2">
            {demoSteps.map((s, index) => (
              <button
                key={s.id}
                onClick={() => goToStep(index)}
                className={`relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full transition-all ${
                  index === currentStep 
                    ? `bg-gradient-to-br ${step.accentColor} text-white shadow-lg` 
                    : index < currentStep
                    ? "bg-emerald-500/30 text-emerald-400"
                    : "bg-white/10 text-white/40 hover:bg-white/20"
                }`}
              >
                {index < currentStep ? (
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <span className="text-xs sm:text-sm font-semibold">{index + 1}</span>
                )}
              </button>
            ))}
          </div>

          {/* Progress bar for current step */}
          <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
            <motion.div
              className={`h-full bg-gradient-to-r ${step.accentColor}`}
              style={{ width: `${(currentStep / demoSteps.length) * 100 + (progress / demoSteps.length)}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 pb-2 min-h-[280px] sm:min-h-[380px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3 }}
            >
              {/* Step Header */}
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring" }}
                  className={`p-2 sm:p-3 rounded-xl bg-gradient-to-br ${step.accentColor} shadow-lg flex-shrink-0`}
                >
                  <StepIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </motion.div>
                <div className="flex-1 min-w-0">
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className={`text-xs sm:text-sm font-medium bg-gradient-to-r ${step.accentColor} bg-clip-text text-transparent`}
                  >
                    Step {step.stepNumber}: {step.subtitle}
                  </motion.p>
                  <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-lg sm:text-2xl font-bold text-white"
                  >
                    {step.title}
                  </motion.h3>
                  
                  {/* Title translations */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.22 }}
                    className="flex flex-wrap gap-x-2 gap-y-1 text-slate-400 text-base mt-1"
                  >
                    <span className="font-hindi">{step.titleTranslations.hi}</span>
                    <span className="text-slate-500">•</span>
                    <span className="font-punjabi">{step.titleTranslations.pa}</span>
                    <span className="text-slate-500">•</span>
                    <span className="font-bengali">{step.titleTranslations.bn}</span>
                  </motion.div>
                </div>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="text-sm sm:text-base text-slate-300 mb-2 leading-relaxed"
              >
                {step.description}
              </motion.p>
              
              {/* Description translations */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.27 }}
                className="flex flex-wrap gap-x-2 gap-y-1 text-slate-500 text-sm mb-4 sm:mb-6"
              >
                <span className="font-hindi">{step.descriptionTranslations.hi}</span>
                <span className="text-slate-600">•</span>
                <span className="font-punjabi">{step.descriptionTranslations.pa}</span>
                <span className="text-slate-600">•</span>
                <span className="font-bengali">{step.descriptionTranslations.bn}</span>
              </motion.div>

              {/* Mock UI */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-3 sm:p-4 rounded-xl bg-slate-800/50 border border-white/10"
              >
                {step.mockContent}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer Controls */}
        <div className="p-4 sm:p-6 pt-3 sm:pt-4 border-t border-white/10">
          {/* Mobile Layout */}
          <div className="flex flex-col gap-3 sm:hidden">
            {/* Row 1: Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                onClick={goPrev}
                className="text-slate-400 hover:text-white hover:bg-white/10"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <span className="text-sm text-slate-400">
                {currentStep + 1} / {demoSteps.length}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={goNext}
                className="text-slate-400 hover:text-white hover:bg-white/10"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Row 2: Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    tryPlayMusic();
                    setIsAutoPlaying(!isAutoPlaying);
                  }}
                  className="h-8 w-8 text-slate-400 hover:text-white hover:bg-white/10"
                >
                  {isAutoPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                
                <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5">
                  <SoundWaveIndicator isPlaying={isMusicPlaying && !isMuted && volume > 0} />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-slate-400 hover:text-white hover:bg-white/10"
                    onClick={() => {
                      tryPlayMusic();
                      setIsMuted(!isMuted);
                    }}
                  >
                    {isMuted || volume === 0 ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
              
              {currentStep === demoSteps.length - 1 ? (
                <Button
                  size="sm"
                  onClick={onClose}
                  className={`bg-gradient-to-r ${step.accentColor} hover:opacity-90`}
                >
                  Start Now
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={goNext}
                  className="text-slate-300 hover:text-white hover:bg-white/10"
                >
                  Next
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden sm:flex items-center justify-between">
            {/* Left: Playback controls */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  tryPlayMusic();
                  setIsAutoPlaying(!isAutoPlaying);
                }}
                className="text-slate-400 hover:text-white hover:bg-white/10"
              >
                {isAutoPlaying ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                {isAutoPlaying ? "Pause" : "Play"}
              </Button>
              
              {/* Volume Control with Music Indicator */}
              <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-white/5">
                <SoundWaveIndicator isPlaying={isMusicPlaying && !isMuted && volume > 0} />
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white hover:bg-white/10"
                  onClick={() => {
                    tryPlayMusic();
                    setIsMuted(!isMuted);
                  }}
                >
                  {isMuted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  onValueChange={handleVolumeChange}
                  max={1}
                  step={0.05}
                  className="w-20"
                  trackClassName="h-1.5 bg-white/20"
                  rangeClassName="bg-gradient-to-r from-purple-500 to-indigo-500"
                  thumbClassName="h-4 w-4 border-0 bg-white shadow-lg shadow-purple-500/30 hover:scale-110 transition-transform"
                />
                
                {!isMusicPlaying && (
                  <span className="text-xs text-slate-500">Click to play</span>
                )}
              </div>
            </div>

            {/* Center: Step Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={goPrev}
                className="text-slate-400 hover:text-white hover:bg-white/10"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <span className="text-sm text-slate-400 w-12 text-center">
                {currentStep + 1} / {demoSteps.length}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={goNext}
                className="text-slate-400 hover:text-white hover:bg-white/10"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-slate-400 hover:text-white hover:bg-white/10"
              >
                Skip Tour
              </Button>
              {currentStep === demoSteps.length - 1 ? (
                <Button
                  onClick={onClose}
                  className={`bg-gradient-to-r ${step.accentColor} hover:opacity-90`}
                >
                  Start Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={goNext}
                  className="text-slate-300 hover:text-white hover:bg-white/10"
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
